import {
  init as sentryInit,
  captureException as sentryCaptureException,
  addBreadcrumb as sentryAddBreadcrumb,
  setTags as sentrySetTags,
  setContext,
  httpClientIntegration,
  httpContextIntegration,
  extraErrorDataIntegration,
} from "@sentry/react";
import type { SeverityLevel as SentrySeverityLevel } from "@sentry/react";

import {
  ALLOW_URLS,
  CONSOLE_LOG_IDENTIFIER,
  DENY_URLS,
  FAILED_REQUEST_STATUS_CODES,
  FAILED_REQUEST_TARGETS,
  IGNORE_ERRORS,
} from "./constants";
import { beforeSend } from "./utils";
import type { Primitive, SentryTag } from "./types";

export type { Primitive, SentryTag };

/**
 * A light wrapper around Sentry, so we can augment its methods to be more
 * useful to our needs.
 */
class ErrorTracker {
  constructor() {
    sentryInit({
      dsn: "https://19707ff4ff7f420b9369a3e15041c787@o227377.ingest.sentry.io/6677584",
      tracesSampleRate: import.meta.env.VITE_SENTRY_TRACE_SAMPLE_RATE || 0.01,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.5,
      environment: import.meta.env.MODE,
      enabled:
        import.meta.env.MODE === "production" ||
        import.meta.env.VITE_SENTRY_ENABLED === "true",
      ignoreErrors: IGNORE_ERRORS,
      denyUrls: DENY_URLS,
      allowUrls: ALLOW_URLS,
      // Source maps resolve via Debug IDs injected by @sentry/vite-plugin, so
      // resolution doesn't depend on these. They should still stay aligned with
      // the "Create Sentry release" step in .github/workflows/release.yml:
      //   release ← action-release `version` (the sdks-app package version)
      //   dist    ← action-release `dist`    (the commit SHA)
      release: FULL_APP_VERSION, // == npm_package_version (see vite.config.ts)
      dist: VITE_COMMIT_SHA, // == `git rev-parse HEAD`, trimmed
      integrations: [
        httpClientIntegration({
          failedRequestStatusCodes: FAILED_REQUEST_STATUS_CODES,
          failedRequestTargets: FAILED_REQUEST_TARGETS,
        }),
        httpContextIntegration(),
        extraErrorDataIntegration(),
      ],
      beforeSend,
      sendDefaultPii: true,
      // Default is 3, but we have a lot of nested objects.
      normalizeDepth: 5,
    });

    setContext("LocalLogic", {
      referrer: document.referrer,
      sdks_app_version: FULL_APP_VERSION,
      sdks_app_path: window?.location?.pathname?.substring(
        // The ?? -1 avoids "Unsafe arithmetic operation on optional chaining. It
        // can result in NaN."
        (window?.location?.pathname?.lastIndexOf("/") ?? -1) + 1,
      ),
    });
  }

  public captureException(error: Error) {
    this.log(error.message, "error");

    sentryCaptureException(error);
  }

  public addBreadCrumb(
    category: string,
    message: string,
    data?: object,
    level: SentrySeverityLevel = "info",
  ) {
    sentryAddBreadcrumb({
      category,
      message,
      data,
      level,
    });
  }

  public setTags(tags: SentryTag) {
    sentrySetTags(tags);
  }

  /**
   * Generate useful console logs for debugging
   */
  private log(message: string, type: "error" | "warn" | "info" = "info") {
    console[type](CONSOLE_LOG_IDENTIFIER + message);
  }
}

const singletonErrorTracker = Object.freeze(new ErrorTracker());
export default singletonErrorTracker;
